/**
 * postMessage bridge for the booking widget when embedded as an iframe.
 *
 * Partners depend on the message envelope below — keep it stable:
 *
 *   { source: 'simplytelehealth', type: 'resize', height: <integer pixels> }
 *   { source: 'simplytelehealth', type: 'ready' }
 *
 * Height never shrinks. We seed the very first resize at MIN_EMBED_HEIGHT so
 * the parent iframe is sized for the tallest step (the confirm card with
 * SMS consent + a validation error measures ~1018px) before the patient
 * gets there — no jumping as they advance. If real content ever exceeds
 * the floor we report the larger value; we never go back below it.
 *
 * No-ops when the page is not iframed (window.parent === window).
 */
const ENVELOPE_SOURCE = 'simplytelehealth';

/**
 * Floor for the reported iframe height. Set above the tallest step we've
 * measured (confirm + SMS + validation error ≈ 1018px) with margin for
 * future additions. Bump if the confirm card gets taller.
 */
const MIN_EMBED_HEIGHT = 1100;

let lastHeight = 0;
let rafId: number | null = null;
let initialized = false;

function postHeight() {
  if (rafId !== null) return;
  rafId = window.requestAnimationFrame(() => {
    rafId = null;
    // Use body.scrollHeight, not documentElement's. Inside an iframe, the
    // html element is sized to the iframe's viewport and clamps the
    // measurement, so documentElement.scrollHeight is whatever the parent
    // allocated, not the actual content height. body.scrollHeight reflects
    // the rendered content.
    const measured = Math.ceil(document.body.scrollHeight);
    // Floor at MIN_EMBED_HEIGHT so the iframe is sized for the tallest step
    // up front, and never shrink below the highest value we've sent.
    const height = Math.max(measured, MIN_EMBED_HEIGHT);
    if (height <= lastHeight) return;
    lastHeight = height;
    window.parent.postMessage(
      { source: ENVELOPE_SOURCE, type: 'resize', height: lastHeight },
      '*',
    );
  });
}

export function initEmbedAutoResize(): () => void {
  // Only run when actually embedded as an iframe, and only once.
  if (typeof window === 'undefined' || window.parent === window || initialized) {
    return () => {};
  }
  initialized = true;

  // Standalone app uses `body { min-height: 100vh }` so the bg color fills the
  // viewport. Inside an iframe, 100vh resolves to whatever height the parent
  // set on the iframe — which makes documentElement.scrollHeight a constant
  // pegged to the iframe's allocated height instead of tracking real content.
  // Override so content drives the measurement.
  const previousMinHeight = document.body.style.minHeight;
  document.body.style.minHeight = '0';

  // Announce readiness so partners can hide a loading spinner.
  window.parent.postMessage({ source: ENVELOPE_SOURCE, type: 'ready' }, '*');

  postHeight();

  const ro = new ResizeObserver(postHeight);
  ro.observe(document.body);

  // Catch route changes / step changes inside the SPA, plus any async content
  // that mutates layout after first paint.
  const mo = new MutationObserver(postHeight);
  mo.observe(document.body, { childList: true, subtree: true });

  // Fonts and images can shift layout after the first paint.
  window.addEventListener('load', postHeight);

  return () => {
    ro.disconnect();
    mo.disconnect();
    window.removeEventListener('load', postHeight);
    if (rafId !== null) {
      window.cancelAnimationFrame(rafId);
      rafId = null;
    }
    document.body.style.minHeight = previousMinHeight;
    initialized = false;
    lastHeight = 0;
  };
}
